/**
 * src/net/net.ts — WebSocket transport for Dune Racer multiplayer.
 *
 * Thin, typed wrapper over the protocol in the repo's SPEC.md. Owns nothing
 * about the game: it connects to /ws/duneracer, (re)sends JSON messages and
 * fans incoming messages out to registered handlers.
 */

export interface Cosmetics {
  badge?: string; name_color?: string; title?: string; name_fx?: string
}
export interface RosterEntry {
  id: string; name: string; host: boolean; spectator: boolean
  /** Equipped store cosmetics, null for guests. */
  cosmetics?: Cosmetics | null
}

export interface JoinedMsg {
  type: 'joined'; id: string; is_host: boolean; spectator: boolean;
  game_state: string; players: RosterEntry[]; max_players: number;
  settings: { bots: number; timeOfDay: string }; round_max_sec: number;
}
export interface LobbyUpdateMsg {
  type: 'lobby_update'; players: RosterEntry[]; host: string | null;
  game_state: string; max_players: number;
  settings: { bots: number; timeOfDay: string }; round_max_sec: number;
}
export interface GameStartingMsg {
  type: 'game_starting'; seed: number;
  stage: { bots: number; timeOfDay: string; showcase: boolean; roundMaxSec: number };
  players: { id: string; name: string }[];
}
export interface LoadingMsg {
  type: 'loading'; players: { id: string; name: string; pct: number; loaded: boolean }[];
  all_loaded: boolean;
}
export interface CountdownMsg { type: 'countdown'; count: number }
export interface StateRacer {
  id: string; x: number; y: number; z: number; h: number; v: number;
  s: number; bot: boolean; finished: boolean; place: number | null;
}
export interface StateMsg {
  type: 'state'; t: number; clock: number; route_len: number;
  racers: StateRacer[]; seed?: number; time_of_day?: string;
}
export interface EventMsg {
  type: 'event';
  event: { kind: string; player_id?: string; code?: string; place?: number; time?: number };
}
export interface MatchEndMsg {
  type: 'match_end'; winner: string | null;
  results: FunStatsResults;
}
export interface FunStatsResults {
  columns: { key: string; label: string }[];
  rows: Record<string, unknown>[];
  awards: { title: string; icon: string; player_id: string | null;
            player_name: string; value: string }[];
  round_capped?: boolean;
}

export type ServerMsg =
  | JoinedMsg | LobbyUpdateMsg | GameStartingMsg | LoadingMsg
  | CountdownMsg | StateMsg | EventMsg | MatchEndMsg
  | { type: string; [k: string]: unknown };

type Handler = (msg: any) => void;

const NET_DEBUG = new URLSearchParams(location.search).has('netdebug')

/** WS URL for the page's own origin — nginx proxies /ws/duneracer. */
export function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws/duneracer`;
}

export class DuneNet {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  onClose: (() => void) | null = null;

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl())
      this.ws = ws
      ws.onopen = () => resolve()
      ws.onerror = () => reject(new Error('websocket failed'))
      ws.onclose = () => { if (this.onClose) this.onClose() }
      ws.onmessage = (ev) => {
        let msg: ServerMsg
        try { msg = JSON.parse(ev.data as string) }
        catch { return }
        if (NET_DEBUG) console.log('[net-in]', (msg as { type: string }).type)
        const set = this.handlers.get((msg as { type: string }).type)
        if (set) for (const h of set) h(msg)
        const all = this.handlers.get('*')
        if (all) for (const h of all) h(msg)
      }
    })
  }

  on(type: string, handler: Handler): void {
    let set = this.handlers.get(type)
    if (!set) { set = new Set(); this.handlers.set(type, set) }
    set.add(handler)
  }

  send(obj: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      if (NET_DEBUG) {
        console.log('[net-out]', (obj as { type?: string }).type ?? '?')
      }
      this.ws.send(JSON.stringify(obj))
    } else if (NET_DEBUG) {
      console.log('[net!] send while closed:', (obj as { type?: string }).type)
    }
  }

  get open(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN
  }
}
